import type { VehicleBlueprint } from "../types/VehicleBlueprint.js";
import type { CarStats, CarUpgrade, StatDamage } from "./CarStats.js";
import { DEFAULT_UPGRADES } from "./CarStats.js";
import { blueprintToBaseStats } from "./defaultStats.js";
import { UPGRADE_MODIFIERS } from "./upgradeTable.js";

/**
 * Compute the final runtime CarStats for a vehicle.
 *
 * Formula per stat:
 *   finalStat = baseStat
 *             × (product of all applicable upgrade multipliers)
 *             × (damage factor, clamped to min 0.1)
 *
 * Damage is applied last so it degrades the upgraded value, not the base.
 *
 * @param blueprint  The vehicle's design blueprint.
 * @param upgrades   Optional upgrade levels. Defaults to all-zero (stock).
 * @param damage     Optional stat damage. Absent fields default to 1.0 (undamaged).
 */
export function computeStats(
  blueprint: VehicleBlueprint,
  upgrades: CarUpgrade = DEFAULT_UPGRADES,
  damage: StatDamage = {},
): CarStats {
  const result = blueprintToBaseStats(blueprint) as Record<keyof CarStats, number>;

  // Apply upgrade multipliers
  for (const slot of Object.keys(upgrades) as (keyof CarUpgrade)[]) {
    const level = upgrades[slot];
    if (level === 0) continue;
    const slotMods = UPGRADE_MODIFIERS[slot];
    for (const statKey of Object.keys(slotMods) as (keyof CarStats)[]) {
      const levelMods = slotMods[statKey];
      if (levelMods === undefined) continue;
      result[statKey] *= levelMods[level - 1];
    }
  }

  // Apply damage multipliers — clamped to 0.1 floor to prevent zero/negative stats
  for (const statKey of Object.keys(damage) as (keyof CarStats)[]) {
    const penalty = damage[statKey];
    if (penalty === undefined) continue;
    result[statKey] *= Math.max(0.1, penalty);
  }

  return result as CarStats;
}
